import { useState, useEffect, useRef, useCallback } from 'react';
import { Navigation, MapPin, Loader2, CheckCircle, XCircle, Smartphone } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function TravelerActivate({ shareId, travelerToken }) {
  const [status, setStatus] = useState('loading'); // loading, ready, tracking, error, inactive
  const [tripInfo, setTripInfo] = useState(null);
  const [posCount, setPosCount] = useState(0);
  const [lastUpdate, setLastUpdate] = useState(null);
  const watchRef = useRef(null);

  useEffect(() => {
    const verify = async () => {
      try {
        const res = await axios.get(`${API}/trip/${shareId}/verify-traveler?token=${travelerToken}`);
        setTripInfo(res.data);
        setStatus(res.data.is_active ? 'ready' : 'inactive');
      } catch {
        setStatus('error');
      }
    };
    verify();
  }, [shareId, travelerToken]);

  const sendPosition = useCallback(async (pos) => {
    try {
      await axios.post(`${API}/trip/${shareId}/traveler-location`, {
        token: travelerToken,
        lat: pos.coords.latitude,
        lng: pos.coords.longitude,
        accuracy: pos.coords.accuracy,
      });
      setPosCount(c => c + 1);
      setLastUpdate(new Date().toLocaleTimeString('it-IT'));
    } catch (e) {
      console.error('Position send error:', e);
    }
  }, [shareId, travelerToken]);

  const startTracking = useCallback(() => {
    if (!navigator.geolocation) {
      alert('Geolocalizzazione non supportata dal browser');
      return;
    }
    setStatus('tracking');

    navigator.geolocation.getCurrentPosition(
      (pos) => sendPosition(pos),
      (err) => console.error('Initial position error:', err),
      { enableHighAccuracy: true }
    );

    watchRef.current = navigator.geolocation.watchPosition(
      (pos) => sendPosition(pos),
      (err) => console.error('Watch error:', err),
      { enableHighAccuracy: true, maximumAge: 30000, timeout: 30000 }
    );
  }, [sendPosition]);

  const stopTracking = useCallback(() => {
    if (watchRef.current !== null) {
      navigator.geolocation.clearWatch(watchRef.current);
      watchRef.current = null;
    }
    setStatus('ready');
  }, []);

  useEffect(() => {
    return () => {
      if (watchRef.current !== null) {
        navigator.geolocation.clearWatch(watchRef.current);
      }
    };
  }, []);

  if (status === 'loading') {
    return (
      <div className="min-h-screen bg-[#0a0e14] flex items-center justify-center">
        <Loader2 className="w-10 h-10 text-teal-400 animate-spin" />
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen bg-[#0a0e14] flex items-center justify-center p-6">
        <div className="text-center">
          <XCircle className="w-16 h-16 text-rose-400 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-white mb-2">Link non valido</h2>
          <p className="text-slate-400">Questo link di attivazione non e valido o e scaduto.</p>
        </div>
      </div>
    );
  }

  if (status === 'inactive') {
    return (
      <div className="min-h-screen bg-[#0a0e14] flex items-center justify-center p-6">
        <div className="text-center">
          <MapPin className="w-16 h-16 text-slate-600 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-white mb-2">Viaggio disattivato</h2>
          <p className="text-slate-400">La condivisione per questo viaggio e stata disattivata.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0e14] flex items-center justify-center p-6" data-testid="traveler-activate-page">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-teal-500/10 border border-teal-500/20 mb-4">
            <Navigation className="w-10 h-10 text-teal-400" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-1">{tripInfo?.trip_name || 'Il mio viaggio'}</h1>
          <p className="text-teal-400 text-sm">
            {tripInfo?.traveler_name}{tripInfo?.destination ? ` - ${tripInfo.destination}` : ''}
          </p>
        </div>

        {/* Main action */}
        {status === 'ready' && (
          <div className="bg-white/5 border border-teal-500/20 rounded-2xl p-8 text-center">
            <Smartphone className="w-12 h-12 text-teal-400 mx-auto mb-4" />
            <h2 className="text-lg font-semibold text-white mb-2">Pronto per condividere la posizione</h2>
            <p className="text-slate-400 text-sm mb-6">
              Premi il pulsante qui sotto per attivare il GPS e condividere la tua posizione in tempo reale con i tuoi cari.
            </p>
            <button
              onClick={startTracking}
              className="w-full bg-gradient-to-r from-teal-600 to-teal-500 hover:from-teal-500 hover:to-teal-400 text-white py-4 rounded-xl font-bold text-lg transition-all flex items-center justify-center gap-3 shadow-lg shadow-teal-500/20"
              data-testid="activate-gps-btn"
            >
              <Navigation className="w-5 h-5" />
              Attiva GPS
            </button>
            <p className="text-slate-600 text-xs mt-4">Il browser ti chiedera il permesso per la geolocalizzazione</p>
          </div>
        )}

        {status === 'tracking' && (
          <div className="bg-teal-500/5 border border-teal-500/30 rounded-2xl p-8 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-teal-500/20 mb-4">
              <div className="w-4 h-4 bg-teal-400 rounded-full animate-pulse" />
            </div>
            <h2 className="text-lg font-semibold text-teal-300 mb-2">GPS Attivo</h2>
            <p className="text-slate-400 text-sm mb-2">La tua posizione viene condivisa in tempo reale</p>
            
            <div className="bg-black/30 rounded-xl p-4 my-6 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Posizioni inviate:</span>
                <span className="text-teal-400 font-mono font-bold">{posCount}</span>
              </div>
              {lastUpdate && (
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Ultimo aggiornamento:</span>
                  <span className="text-slate-300 font-mono">{lastUpdate}</span>
                </div>
              )}
            </div>

            <p className="text-slate-500 text-xs mb-6">Tieni questa pagina aperta per continuare a condividere la posizione</p>

            <button
              onClick={stopTracking}
              className="w-full bg-rose-500/20 hover:bg-rose-500/30 border border-rose-500/30 text-rose-300 py-3 rounded-xl font-medium transition-all flex items-center justify-center gap-2"
              data-testid="stop-gps-btn"
            >
              <CheckCircle className="w-4 h-4" />
              Interrompi condivisione
            </button>
          </div>
        )}

        {/* Footer */}
        <p className="text-center text-slate-700 text-xs mt-8">
          Powered by eSIM4You - Made With Love By TelcaVoIP AI
        </p>
      </div>
    </div>
  );
}
